import { AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { VariantGroup } from 'base/library_service';
import { Button, ButtonPalette, RadioButtonGroupWrapper } from 'base/ui/button';
import {
  IconBottom,
  IconIcon,
  IconLeft,
  IconMap,
  IconNone,
  IconPng,
  IconRight,
  IconTextCenter,
  IconTextLeft,
  IconTextRight,
  IconTop,
  IconVideo,
} from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { menuItem } from 'base/ui/menu_model';
import { SelectButton } from 'base/ui/select';
import { TextSmallLight } from 'base/ui/typography';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import {
  RemoveSingleSuggestion,
  SingleSuggestionStore,
} from '../add_panel/suggestions';
import { Assistant } from '../ai/assistant';
import { makeReplaceComponentSuggestion } from '../ai/component_content_mapping';
import { Page, Section, SectionRef } from '../model';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import {
  commitSuggestion,
  ReplaceComponentSuggestion,
} from '../suggested_edits';
import styles from './variants.css';

/** A list of rows, showing different variants on the selected component */
export const VariantToggles = observer(function _VariantToggles({
  page,
  sref,
  section,
  library,
  session,
  selection,
  assistant,
  analytics,
}: {
  page: Page;
  sref: SectionRef;
  section: Section;
  library: LibraryCache;
  session: ProjectSession;
  selection: SelectionStore;
  assistant: Assistant;
  analytics: AnalyticsService;
}) {
  const [suggestionStore] = React.useState(() => new SingleSuggestionStore());

  const currentSlug = section.element.current?.source?.slug;
  const meta = currentSlug
    ? library.getCachedPublicComponent(currentSlug, {
        fetchIfMissing: true,
      })
    : undefined;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const variantGroups = meta?.value?.variants || [];

  const rows = React.useMemo(
    () =>
      variantGroups
        .filter((g) => g.items.length > 1)
        .map((group) =>
          group.items.map(
            (item) =>
              new VariantItemModelImpl(
                item,
                group.kind,
                { page, sref, section },
                suggestionStore,
                selection,
                assistant,
                session,
                library,
                analytics,
              ),
          ),
        ),
    [
      variantGroups,
      page,
      sref,
      section,
      suggestionStore,
      selection,
      assistant,
      session,
      library,
      analytics,
    ],
  );

  if (rows.length === 0) return null;
  return (
    <Rows spacing={8}>
      {rows.map((items, i) => (
        <VariantTogglesRow key={i} items={items} />
      ))}
    </Rows>
  );
});

export function VariantTogglesRow({ items }: { items: VariantItemModel[] }) {
  if (items.length === 0) return null;
  const Comp =
    items.length <= 2 || items.every((i) => i.icon || i.label.length <= 2)
      ? RadioVaraintGroup
      : DropdownVariantGroup;
  return (
    <LabelledOption label={items[0].groupKind}>
      <Comp items={items} />
    </LabelledOption>
  );
}

export function LabelledOption({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.labelledOption}>
      <TextSmallLight color="grey">{label}</TextSmallLight>
      {children}
    </div>
  );
}

export interface VariantItemModel {
  readonly groupKind: string;
  readonly isSelected: boolean;
  readonly label: string;
  readonly icon: React.ComponentType | React.ReactNode | undefined;
  handleClick(): void;
  handleMouseEnter(): void;
  handleMouseLeave(): void;
  handleUnmount(): void;
}

/**
 * A variant item, which can be selected or hovered (suggestion).
 *
 * It can be bound to a radio button or a menu item.  Both of those use a
 * totally different api hence the need for a model class.
 **/
class VariantItemModelImpl implements VariantItemModel {
  removeMySuggestion: RemoveSingleSuggestion | undefined;
  preloadTimeout: number | undefined;

  constructor(
    private readonly item: VariantGroup['items'][0],
    readonly groupKind: string,
    private readonly location: {
      page: Page;
      sref: SectionRef;
      section: Section;
    },
    private readonly suggestionStore: SingleSuggestionStore,
    private readonly selection: SelectionStore,
    private readonly assistant: Assistant,
    private readonly session: ProjectSession,
    private readonly library: LibraryCache,
    private readonly analytics: AnalyticsService,
  ) {
    makeObservable(this);
  }

  @computed
  get isSelected() {
    const source = this.location.section.element.current?.source;
    return source?.slug === this.item.componentSlug;
  }

  @computed
  private get suggestion(): ReplaceComponentSuggestion {
    return {
      ...makeReplaceComponentSuggestion(
        this.location.section,
        this.item.componentSlug,
        this.library,
      ),
      ...this.location,
      defaultName: '',
      defaultDescription: '',
    };
  }

  readonly handleClick = () => {
    if (this.isSelected) return;
    this.suggestionStore.clear();
    if (!this.session.canViewWireframe(this.suggestion.page)) {
      this.session.showUpgradeWall?.('pages');
      return;
    }
    const { suggestion } = this;
    const { section, sref } = suggestion;
    const oldInstance = section.element.current?.state;
    this.analytics.track({
      event: 'blocks/add-section',
      method: 'variant',
      interaction: 'replace',
      name: section.name,
      isGlobal: sref.type === 'reference',
      previousComponentSlug: oldInstance ? oldInstance.source?.slug : undefined,
      componentSlug: this.item.componentSlug,
      variantGroup: this.groupKind,
      variantGroupItem: this.item.kind,
    });
    commitSuggestion(suggestion, this.selection, this.library);
    if (suggestion.needsToGenerateCopy) {
      this.assistant.generateCopy(new Map([[sref, oldInstance]]));
    }
  };

  readonly handleMouseEnter = () => {
    clearTimeout(this.preloadTimeout);
    this.suggestionStore.clear();
    if (!this.isSelected) {
      this.removeMySuggestion = this.suggestionStore.set(() => this.suggestion);
      // Preload the component metadata, so the variants panel is
      // ready to go when the user clicks.
      this.preloadTimeout = setTimeout(() => {
        this.library.getCachedPublicComponent(this.item.componentSlug, {
          fetchIfMissing: true,
        });
      }, 200);
    }
  };

  readonly handleMouseLeave = () => {
    clearTimeout(this.preloadTimeout);
    // a delay to reduce flickering when mousing between buttons
    this.removeMySuggestion?.({ delayMs: 100 });
  };

  readonly handleUnmount = () => {
    clearTimeout(this.preloadTimeout);
    this.removeMySuggestion?.();
  };

  readonly label = this.item.kind;

  get icon() {
    return ICONS_FOR_VARIANTS[this.groupKind.toLowerCase()]?.[
      this.label.toLowerCase()
    ];
  }
}

const RadioVaraintGroup = observer(function _ComponentVariantButton({
  items,
}: {
  items: readonly VariantItemModel[];
}) {
  React.useEffect(
    () => () => items.forEach((item) => item.handleUnmount()),
    [items],
  );
  return (
    <ButtonPalette>
      <RadioButtonGroupWrapper>
        {items.map((item) => (
          <RadioVariantButton key={keyOfObject(item)} item={item} />
        ))}
      </RadioButtonGroupWrapper>
    </ButtonPalette>
  );
});

const RadioVariantButton = observer(function _RadioVariantButton({
  item,
}: {
  item: VariantItemModel;
}) {
  const [justMounted, setJustMounted] = React.useState(true);
  React.useEffect(() => {
    requestAnimationFrame(() => setJustMounted(false));
  }, []);
  const icon = item.icon;

  return (
    <Button
      onClick={item.handleClick}
      // If the user's switching variants, their mouse might "enter" a new button
      // because the layout shifted.  We don't want that to trigger a suggestion,
      // so ignore mouse enter events for the first frame.
      onMouseEnter={justMounted ? undefined : item.handleMouseEnter}
      onMouseLeave={item.handleMouseLeave}
      variant={item.isSelected ? 'secondary' : 'subtle'}
      subtlyDisabled={item.isSelected}
      labelNoWrap
      labelNoPadding
      tooltipPlacement="vertical"
      {...(icon
        ? {
            label: undefined,
            icon,
            tooltip: item.label,
          }
        : {
            label: item.label,
            icon: undefined,
            tooltip: undefined,
          })}
    />
  );
});

const DropdownVariantGroup = observer(function _ComponentVariantDropdown({
  items,
}: {
  items: VariantItemModel[];
}) {
  React.useEffect(
    () => () => items.forEach((item) => item.handleUnmount()),
    [items],
  );
  const menuModel = items.map((item) =>
    menuItem({
      onClick: item.handleClick,
      onMouseEnter: item.handleMouseEnter,
      onMouseLeave: item.handleMouseLeave,
      label: item.label,
      icon: item.icon,
      checked: item.isSelected,
    }),
  );
  return (
    <SelectButton
      menu={menuModel}
      onClose={() => items.forEach((i) => i.handleUnmount())}
    />
  );
});

export const ICONS_FOR_VARIANTS: Record<
  string,
  Record<string, React.ComponentType>
> = {
  text: {
    left: IconTextLeft,
    center: IconTextCenter,
    right: IconTextRight,
  },
  'asset placement': {
    left: IconLeft,
    right: IconRight,
    top: IconTop,
    bottom: IconBottom,
  },
  asset: {
    none: IconNone,
    image: IconPng,
    icon: IconIcon,
    video: IconVideo,
    map: IconMap,
  },
  background: {
    none: IconNone,
    image: IconPng,
    video: IconVideo,
  },
};
